import type { ComboboxItem } from '@/components'
import { Combobox, Field, FieldLabel, PromptModal } from '@/components'
import { ExportFormat } from './consts'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'

interface Props {
  onSubmit: PromptResolve<ExportFormat | null>
}

/**
 * Picks the file format for a list export. Excel and PDF are the only two that
 * make sense for a table of rows, so there is nothing else to ask — where the
 * file ends up follows from the format, the way it does for the equipment
 * export.
 */
export const ExportFormatPrompt: React.FC<Props> = ({ onSubmit }) => {
  const translations = useTranslate({
    export: 'common.export',
    exportFormat: 'common.export-format',
    excel: 'common.excel',
    pdf: 'common.pdf',
  })

  const [exportFormat, setExportFormat] = useState(ExportFormat.EXCEL)

  const items = useMemo<ComboboxItem<ExportFormat>[]>(
    () => [
      { value: ExportFormat.EXCEL, label: translations.excel },
      { value: ExportFormat.PDF, label: translations.pdf },
    ],
    [translations.excel, translations.pdf]
  )

  return (
    <PromptModal
      title={translations.export}
      submitText={translations.export}
      cancelOnClickOutside
      cancelOnEscape
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(exportFormat)}
    >
      <Field>
        <FieldLabel>{translations.exportFormat}</FieldLabel>
        <Combobox<ExportFormat>
          mode="single"
          value={exportFormat}
          items={items}
          noSearch
          onChange={value => setExportFormat(value ?? ExportFormat.EXCEL)}
        />
      </Field>
    </PromptModal>
  )
}
